import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  House, Briefcase, ChatCircle, Wallet, DotsThreeCircle, MapTrifold,
  Calculator, PlusCircle, FolderOpen, Translate
} from '@phosphor-icons/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer';
import { haptic } from '../utils/haptics';
import axiosInstance from '../api/api';
import { MEMBER_MENUS, SUPPORT_LINKS } from './MembersFooter';

const TABS = {
  worker: [
    { icon: House, label: 'Home', path: '/worker-dashboard' },
    { icon: Briefcase, label: 'Jobs', path: '/browse-jobs' },
    { icon: ChatCircle, label: 'Messages', path: '/messages' },
    { icon: Translate, label: 'Translate', path: '/translator' },
    { icon: Wallet, label: 'My Pay', path: '/worker-control-panel?tab=payments' },
  ],
  contractor: [
    { icon: House, label: 'Home', path: '/contractor-dashboard' },
    { icon: MapTrifold, label: 'Leads', path: '/leads-marketplace' },
    { icon: ChatCircle, label: 'Messages', path: '/messages' },
    { icon: Translate, label: 'Translate', path: '/translator' },
    { icon: Calculator, label: 'Pricing', path: '/ai-pricing' },
  ],
  homeowner: [
    { icon: House, label: 'Home', path: '/homeowner-dashboard' },
    { icon: PlusCircle, label: 'Post Job', path: '/post-job' },
    { icon: ChatCircle, label: 'Messages', path: '/messages' },
    { icon: FolderOpen, label: 'Projects', path: '/my-jobs' },
  ],
};

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const userType = localStorage.getItem('userType');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token || !TABS[userType]) return;
    const fetchUnread = () => {
      axiosInstance.get('/messages/unread/count')
        .then(({ data }) => setUnread(data?.unread_count || 0))
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [token, userType, location.pathname]);

  if (!token || !TABS[userType]) return null;

  const navItems = TABS[userType];
  const menu = MEMBER_MENUS[userType];
  const roleColor = { contractor: '#EA580C', worker: '#059669', homeowner: '#2B4C6F' }[userType] || '#2563EB';

  const isActive = (path) => {
    const clean = path.split('?')[0];
    if (clean === '/') return location.pathname === '/';
    return location.pathname.startsWith(clean);
  };

  const go = (path) => {
    haptic(8);
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav
        className="ios-tabbar fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 z-50 md:hidden pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-stretch h-[62px]">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                className="relative flex-1 flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform"
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <span className="relative">
                  <Icon size={23} weight={active ? 'fill' : 'regular'} style={active ? { color: roleColor } : undefined} className={active ? '' : 'text-slate-400'} />
                  {item.label === 'Messages' && unread > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none"
                      data-testid="nav-messages-unread-badge"
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </span>
                <span style={active ? { color: roleColor } : undefined} className={`text-[10px] ${active ? 'font-semibold' : 'text-slate-400 font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => { haptic(8); setMoreOpen(true); }}
            className="relative flex-1 flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform"
            data-testid="nav-more"
          >
            <DotsThreeCircle size={23} weight={moreOpen ? 'fill' : 'regular'} style={moreOpen ? { color: roleColor } : undefined} className={moreOpen ? '' : 'text-slate-400'} />
            <span style={moreOpen ? { color: roleColor } : undefined} className={`text-[10px] ${moreOpen ? 'font-semibold' : 'text-slate-400 font-medium'}`}>More</span>
          </button>
        </div>
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerContent data-testid="mobile-more-sheet">
          <DrawerHeader className="text-left pb-1">
            <DrawerTitle className="font-bold">Menu</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-8 overflow-y-auto max-h-[65vh]">
            {[...menu, { title: 'Support', links: SUPPORT_LINKS }].map((col) => (
              <div key={col.title} className="mb-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{col.title}</p>
                <div className="grid grid-cols-2 gap-2">
                  {col.links.map(({ to, label, Icon }) => (
                    <button
                      key={to}
                      onClick={() => go(to)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200 active:bg-slate-100"
                      data-testid={`more-link-${to.replace(/\//g, '')}`}
                    >
                      <Icon size={17} weight="bold" className="text-[#2563EB] flex-shrink-0" />
                      <span className="truncate">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MobileBottomNav;
